import { useTranslation } from 'react-i18next';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import { useMojeSouborySeznamContext } from '@gov-nx/module/page';
import {
	ContentLayout,
	Dialog,
	ErrorStatusBanner,
	FilterParameterList,
	HeaderLayout,
	InfoBanner,
	LayoutGap,
	Pagination,
} from '@gov-nx/ui/web';
import { scrollIntoView } from '@gov-nx/web';
import { MyFilesUploadForm } from '../nahrat/MyFilesUploadForm';
import { Badges } from './Badges';
import { ConfirmPrompts } from './ConfirmPrompts';
import { DocumentsTable } from './DocumentsTable';
import { MyFilesListSkeleton } from './MyFilesSkeleton';
import { UploadSection } from './UploadSection';
import { EmptyBinButton } from './kos/EmptyBinButton';
import { FilterModal } from './soubory/FilterModal';
import { Filters } from './soubory/Filters';

export const MyFilesListView = () => {
	const {
		myFilesModals: { setNoFileModal, noFileModal },
		formDefinition,
		onSubmit,
		documents,
		documentsBinned,
		isSelectedRecycleBin,
		code,
		controls,
		myFilesFilters,
		isDataFetching,
	} = useMojeSouborySeznamContext();

	const { t } = useTranslation([LocalizeNameSpaceTypes.Page]);

	const { t: tsn } = useLocale(code);

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	if (controls.processError) {
		return <ErrorStatusBanner code={code} />;
	}

	const total =
		(isSelectedRecycleBin ? documentsBinned?.pocet : documents?.pocet) ?? 0;
	const filteredTotal =
		(isSelectedRecycleBin
			? documentsBinned?.pocet
			: documents?.seznam?.length) ?? 0;

	return (
		<>
			<div>
				<MyFilesUploadForm />
				<UploadSection />
				<ConfirmPrompts />
			</div>
			<GovFormWeb
				formDefinition={formDefinition}
				onSubmit={onSubmit}>
				<ContentLayout>
					<LayoutGap gap="l">
						{!isSelectedRecycleBin && (
							<HeaderLayout
								filterParams={
									<FilterParameterList
										items={myFilesFilters.filterParams}
										onReset={myFilesFilters.resetFilter}
									/>
								}
								className="flex justify-between gap-(--templates-margin-m)">
								<Filters />
							</HeaderLayout>
						)}

						<Badges />

						{isDataFetching ? (
							<MyFilesListSkeleton />
						) : (
							<div>
								{total === 0 ? (
									<InfoBanner
										headline={tsn('stav.chyba.zatim-tu-nic-nemame')}
										text={tsn('stav.chyba.zda-se-ze-jste-tu-poprve')}
									/>
								) : (
									<div>
										<DocumentsTable />
										<Dialog
											isOpen={!!noFileModal}
											onClose={() => setNoFileModal(null)}
											title={<h3>{noFileModal?.nazev}</h3>}>
											<p>
												{noFileModal?.komentar ?? tsn('data.komentar-neuveden')}
											</p>
										</Dialog>

										<footer className="flex flex-col gap-4 pt-4 text-right">
											{filteredTotal < myFilesFilters.filter.pageSize &&
											myFilesFilters.filter.start === 0 ? null : (
												<Pagination
													color="primary"
													size="m"
													wcag-label={t('strankovani', {
														namespace: LocalizeNameSpaceTypes.Global,
													})}
													total={total}
													pageSize={myFilesFilters.filter.pageSize}
													current={myFilesFilters.pageNumber}
													onPage={(page) => {
														scrollIntoView();
														myFilesFilters.goToPage(page);
													}}
												/>
											)}
											{isSelectedRecycleBin && <EmptyBinButton />}
										</footer>
									</div>
								)}
							</div>
						)}
					</LayoutGap>
				</ContentLayout>
			</GovFormWeb>
			<FilterModal />
		</>
	);
};
